import { useRequest } from 'ahooks';
import { createContext, FC, ReactNode, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { UserNotificationsApiService, UserNotificationsFormApiService } from 'src/api/notifications/notifications-api';
import { NotificationItem } from 'src/api/notifications/types';
import { CommandResult } from 'src/common/api-service';
import container from 'src/config/inversify-config';
import { isNullOrWhiteSpaces } from 'src/shared/utils/strings';

export interface UserNotificationsContextState {
  unseenNotificationsCount: number;
  markAsSeen: (ids?: string[]) => Promise<CommandResult>;
}

const UserNotificationsContext = createContext<UserNotificationsContextState>({
  unseenNotificationsCount: 0,
  markAsSeen: () => Promise.resolve({} as any)
});

export const UserNotificationsProvider: FC<{ pollingInterval?: number; children: ReactNode }> = ({ pollingInterval = 15 * 60 * 1000, children }) => {
  const auth = useAuth();

  const [ready, setReady] = useState<boolean>(false);
  useEffect(() => {
    setReady(!!(auth && !auth.isLoading && auth.isAuthenticated && auth.user && !auth.error));
  }, [auth]);

  const userId = auth.user?.profile?.sub;
  const apiService = container.get<UserNotificationsApiService>(UserNotificationsApiService);
  const formApiService = container.get<UserNotificationsFormApiService>(UserNotificationsFormApiService);

  const getUnseenNotificationsCount = async (isReady: boolean, uId?: string): Promise<number> => {
    if (!isReady || isNullOrWhiteSpaces(uId)) return 0;
    try {
      const response = await apiService.getWithOData<NotificationItem>({
        count: true,
        skip: 0,
        top: 0,
        filter: {
          seen: { eq: false },
          userId: { eq: { value: uId, type: 'raw' } }
        },
        select: ['seen']
      });
      return response?.['@odata.count'] ?? 0;
    } catch (e) {
      console.error('UserNotificationsProvider > getUnseenNotificationsCount', { e });
      throw e;
    }
  };

  const {
    data: unseenNotificationCount,
    cancel,
    runAsync
  } = useRequest(() => getUnseenNotificationsCount(ready, userId), {
    refreshDeps: [ready, userId],
    refreshOnWindowFocus: true,
    pollingInterval: pollingInterval,
    manual: true
  });

  useEffect(() => {
    if (ready) runAsync();
    else cancel();
  }, [ready, runAsync, cancel]);

  useEffect(() => {
    return auth.events.addUserUnloaded(() => {
      cancel();
      setReady(false);
    });
  }, [auth.events, cancel]);

  const markAsSeen = async (ids?: string[]): Promise<CommandResult> => {
    if (!ready || isNullOrWhiteSpaces(userId)) return { isSuccess: false, error: 'user not logged in', identity: JSON.stringify(ids) };

    let result: CommandResult;

    try {
      result = await formApiService.markAsSeen(ids);
    } catch (e) {
      console.error('UserNotificationsProvider > markAsSeen', { e });
      result = { isSuccess: false, error: JSON.stringify(e), identity: JSON.stringify(ids) };
    } finally {
      cancel();
      runAsync();
    }

    return result;
  };

  return (
    <UserNotificationsContext.Provider value={{ unseenNotificationsCount: unseenNotificationCount ?? 0, markAsSeen }}>
      {children}
    </UserNotificationsContext.Provider>
  );
};

export default UserNotificationsContext;
